import { useCallback, useState } from 'react';
import { NoteComment } from '@/types/note';

interface TextSelection {
  start: number;
  end: number;
  text: string;
}

export const useCommentHandler = () => {
  const [comments, setComments] = useState<NoteComment[]>([]);
  const [selectedText, setSelectedText] = useState<TextSelection | null>(null);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [showSelectionError, setShowSelectionError] = useState(false);

  const handleTextSelection = useCallback((
    elementRef: HTMLTextAreaElement | HTMLDivElement | React.RefObject<HTMLTextAreaElement> | null, 
    content: string,
    isWysiwygMode: boolean = false
  ) => {
    let element: HTMLTextAreaElement | HTMLDivElement | null = null;
    
    if (elementRef) {
      // If it's a React ref object
      if ('current' in elementRef) {
        element = elementRef.current;
      } else {
        // If it's a direct element
        element = elementRef;
      }
    }
    
    if (element) {
      let start: number, end: number;
      let selectedTextStr = '';
      
      if (element instanceof HTMLTextAreaElement) {
        start = element.selectionStart;
        end = element.selectionEnd;
        selectedTextStr = content.substring(start, end);
      } else if (element instanceof HTMLDivElement && isWysiwygMode) {
        // For contentEditable divs, use the stored selection info captured from visible text only
        start = (element as any).selectionStart || 0;
        end = (element as any).selectionEnd || 0;
        selectedTextStr = (element as any).selectedText || '';
      } else {
        return;
      }
      
      if (selectedTextStr) {
        setSelectedText({ start, end, text: selectedTextStr });
        // Don't auto-show dialog anymore
      } else if (element instanceof HTMLDivElement && isWysiwygMode) {
        // Caret moved inside the editor without a selection — clear any
        // previously stored selection so AI/comment actions target the
        // caret instead of a stale range.
        setSelectedText(null);
      }
    }
  }, []);

  const handleCommentButtonClick = useCallback((
    element?: HTMLTextAreaElement | HTMLDivElement | null,
    isWysiwygMode: boolean = false
  ) => {
    // Prefer reading the live selection from the element first — focus
    // changes (clicking the toolbar) can drop React state before this
    // handler runs.
    let liveSelection: { start: number; end: number; text: string } | null = null;
    if (element) {
      let text = '';
      let start = 0;
      let end = 0;
      if (element instanceof HTMLTextAreaElement) {
        start = element.selectionStart;
        end = element.selectionEnd;
        text = element.value.substring(start, end);
      } else if (element instanceof HTMLDivElement) {
        // Try the saved (last non-empty) selection captured by the editor's
        // selectionchange handler.
        const savedStart = (element as any).selectionStart;
        const savedEnd = (element as any).selectionEnd;
        const savedText = (element as any).selectedText;
        if (typeof savedText === 'string' && savedText.length > 0) {
          start = savedStart || 0;
          end = savedEnd || 0;
          text = savedText;
        } else {
          // Last resort: read directly from the live window selection.
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            if (element.contains(range.commonAncestorContainer)) {
              const pre = range.cloneRange();
              pre.selectNodeContents(element);
              pre.setEnd(range.startContainer, range.startOffset);
              start = pre.toString().length;
              text = range.toString();
              end = start + text.length;
            }
          }
        }
      }
      if (text) {
        liveSelection = { start, end, text };
      }
    }

    const activeSelection = liveSelection ?? selectedText;
    if (activeSelection) {
      if (liveSelection) setSelectedText(liveSelection);
      setShowCommentDialog(true);
    } else {
      setShowSelectionError(true);
      setTimeout(() => setShowSelectionError(false), 4000);
    }
  }, [selectedText]);


  const handleAddComment = (text: string, replyTo?: string) => {
    if (selectedText) {
      const newComment = {
        id: Date.now().toString(),
        text,
        timestamp: new Date().toLocaleString(),
        replyTo,
        selection: selectedText
      };
      setComments(prev => [...prev, newComment]);
      setShowCommentDialog(false);
      setSelectedText(null);
    }
  };

  const handleDeleteComment = (commentId: string) => {
    setComments(prev => prev.filter(comment => comment.id !== commentId));
  };

  return {
    comments,
    selectedText,
    showCommentDialog,
    showSelectionError,
    setShowCommentDialog,
    handleTextSelection,
    handleAddComment,
    handleDeleteComment,
    handleCommentButtonClick,
    setComments,
  };
};
